import { yupResolver } from "@hookform/resolvers/yup";
import { Fade, Grid, Hidden, Typography } from "@material-ui/core";
import Container from "@material-ui/core/Container";
import clsx from "clsx";
import React, { FormEvent, useEffect, useReducer } from "react";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import logo from "../../assets/img/PassAppLogo.png";
import { CardCustom } from "../../components/common/CardCustom";
import SubmitButton from "../../components/common/SubmitButton";
import TextFieldOutlined from "../../components/common/TextFieldOutlined";
import SelectLanguage from "../../components/SelectLanguage/SelectLanguage";
import { useStore } from "../../hooks/useStore";
import { useTranslation } from "../../hooks/useTranslation";
import { login, resetError } from "../../redux/actions/authActions";
import { LoginActionType } from "../../redux/actions/types/loginTypes";
import { version } from "../../../package.json";
import { loginInitialState, loginReducer } from "../../redux/reducers/loginReducer";
import { RoutePaths } from "../../utils/Items";
import { loginSchema } from "../../utils/validations/LoginValidationSquema";
import useStyles from "./LoginStyles";

const Login = () => {
  const classes = useStyles();
  const history = useHistory();
  const { t, BundleKey } = useTranslation();
  const dispatchToStore = useDispatch();
  const [state, dispatch] = useReducer(loginReducer, loginInitialState);
  const { authStore } = useStore();
  const { email, password } = state;
  const {
    register,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(loginSchema(t)),
    mode: "onBlur",
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!errors.email && !errors.password) {
      dispatchToStore(login(email, password));
      setTimeout(() => {
        reset();
      }, 500);
    }
  };

  const handleChange = (value: string, field: string) => {
    dispatch({
      type: LoginActionType.LOGIN_FIELD,
      payload: { fieldName: field, value: value },
    });
  };

  const refreshErrors = () => {
    dispatchToStore(resetError());
  };

  // El operador de reservas se reconoce por el espacio a cargo que trae el login.
  // Va primero porque su alcance también deriva en buildings, igual que el resto
  // de los roles, así que mirar buildings no alcanza para distinguirlo.
  useEffect(() => {
    if (authStore.isAuthenticated) {
      if (authStore.space?.id) {
        history.push(RoutePaths.Reservations);
      } else if (authStore.buildings?.length > 0) {
        history.push(RoutePaths.LogisticParkHome);
      } else {
        history.push(RoutePaths.Dashboard);
      }
    }
  }, [authStore.isAuthenticated, authStore.space, authStore.buildings, history]);

  return (
    <>
      <div className={classes.rectangleBG}>
        <Hidden xsDown>
          <div className={classes.rectangle}></div>
        </Hidden>
        <Hidden smUp>
          <div className={classes.rectangleXs}></div>
        </Hidden>
      </div>
      <div style={{ height: "8vh", display: "flex", justifyContent: "flex-end" }}>
        <SelectLanguage />
      </div>
      <Container className={classes.root} component="main" maxWidth="xs">
        <CardCustom>
          <img alt="Web Logo" src={logo} className={classes.logo} />
          <form className={classes.form} onSubmit={onSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextFieldOutlined
                  name="email"
                  type="text"
                  label={t(BundleKey.LOGIN_INPUT_EMAIL_PLACEHOLDER)}
                  value={email}
                  registration={register("email")}
                  onChange={(e) => handleChange(e.toLowerCase(), "email")}
                  errors={errors.email?.message}
                  onFocus={refreshErrors}
                />
              </Grid>
              <Grid item xs={12}>
                <TextFieldOutlined
                  name="password"
                  type="password"
                  label={t(BundleKey.LOGIN_INPUT_PASSWORD_PLACEHOLDER)}
                  value={password}
                  registration={register("password")}
                  onChange={(value) => handleChange(value, "password")}
                  errors={errors.password?.message}
                  onFocus={refreshErrors}
                />
              </Grid>
              <Grid item className={classes.noPadding}>
                <Fade in={authStore.invalid}>
                  <div
                    className={clsx(
                      {},
                      {
                        [classes.noHeight]: !authStore.invalid,
                      }
                    )}
                  >
                    <p className={classes.error}>{t(BundleKey.LOGIN_ERROR)}</p>
                  </div>
                </Fade>
              </Grid>
              <Grid item xs={12}>
                <SubmitButton color="primary" loading={authStore.loading}>
                  {t(BundleKey.LOGIN_BUTTON_LOGIN)}
                </SubmitButton>
              </Grid>
            </Grid>
            <Grid container direction="row" justifyContent="center" alignItems="center">
              <Grid item xs={12}>
                <p className={classes.forgotPassword}>{t(BundleKey.LOGIN_LABEL_FORGOTPASS)}</p>
              </Grid>
              <Grid item xs={12}>
                <Typography className={classes.link} variant="body2">
                  {t(BundleKey.LOGIN_BUTTON_CONTACT)}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography className={classes.version} variant="body2">
                  {version}
                </Typography>
              </Grid>
            </Grid>
          </form>
        </CardCustom>
      </Container>
    </>
  );
};

export default Login;
